// Statistical Analysis & Inference helpers: confidence intervals, sample
// size/margin of error, t-tests, chi-square goodness-of-fit, p-values,
// correlation/regression, standard error, effect size (Cohen's d), and
// relative risk/odds ratio. Distinct from lib/tools/descriptiveStatisticsCalculators.ts
// (summarizing a single dataset) and lib/tools/probabilityRandomCalculators.ts
// (combinatorics/distributions/randomization) - this file is specifically
// inferential statistics (estimating/testing claims about a population from
// sample data).
import { t } from './i18n'

// ============================================================
// Shared math: normal, Student's t, and chi-square distributions
// ============================================================

// Abramowitz & Stegun erf approximation (max error ~1.5e-7), same approach
// used in lib/tools/probabilityRandomCalculators.ts
function erf(x: number): number {
    const sign = x >= 0 ? 1 : -1
    x = Math.abs(x)
    const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911
    const tt = 1 / (1 + p * x)
    const y = 1 - (((((a5 * tt + a4) * tt) + a3) * tt + a2) * tt + a1) * tt * Math.exp(-x * x)
    return sign * y
}
function normalCDF(x: number): number {
    return 0.5 * (1 + erf(x / Math.SQRT2))
}

// log-Gamma via the Lanczos approximation - needed by both the incomplete
// beta (t-distribution) and incomplete gamma (chi-square) functions below
function logGamma(x: number): number {
    const cof = [76.18009172947146, -86.50532032941677, 24.01409824083091, -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5]
    let y = x
    let tmp = x + 5.5
    tmp -= (x + 0.5) * Math.log(tmp)
    let ser = 1.000000000190015
    for (let j = 0; j < 6; j++) {
        y += 1
        ser += cof[j] / y
    }
    return -tmp + Math.log((2.5066282746310005 * ser) / x)
}

// Regularized incomplete beta function I_x(a,b) via a continued-fraction
// expansion (Numerical Recipes-style) - used to get the Student's t CDF
function betacf(a: number, b: number, x: number): number {
    const MAXIT = 200, EPS = 3e-16, FPMIN = 1e-300
    const qab = a + b, qap = a + 1, qam = a - 1
    let c = 1
    let d = 1 - (qab * x) / qap
    if (Math.abs(d) < FPMIN) d = FPMIN
    d = 1 / d
    let h = d
    for (let m = 1; m <= MAXIT; m++) {
        const m2 = 2 * m
        let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2))
        d = 1 + aa * d
        if (Math.abs(d) < FPMIN) d = FPMIN
        c = 1 + aa / c
        if (Math.abs(c) < FPMIN) c = FPMIN
        d = 1 / d
        h *= d * c
        aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2))
        d = 1 + aa * d
        if (Math.abs(d) < FPMIN) d = FPMIN
        c = 1 + aa / c
        if (Math.abs(c) < FPMIN) c = FPMIN
        d = 1 / d
        const del = d * c
        h *= del
        if (Math.abs(del - 1) < EPS) break
    }
    return h
}
function betainc(x: number, a: number, b: number): number {
    if (x <= 0) return 0
    if (x >= 1) return 1
    const bt = Math.exp(logGamma(a + b) - logGamma(a) - logGamma(b) + a * Math.log(x) + b * Math.log(1 - x))
    if (x < (a + 1) / (a + b + 2)) return (bt * betacf(a, b, x)) / a
    return 1 - (bt * betacf(b, a, 1 - x)) / b
}
// P(T <= t) for Student's t distribution with `df` degrees of freedom
function tCDF(tStat: number, df: number): number {
    const x = df / (df + tStat * tStat)
    const p = 1 - 0.5 * betainc(x, df / 2, 0.5)
    return tStat >= 0 ? p : 1 - p
}
// Two-tailed critical t value for a given confidence level and df, found by
// bisection against tCDF (no closed-form inverse for the t distribution)
function tCritical(confidence: number, df: number): number {
    const targetP = 1 - (1 - confidence) / 2
    let lo = 0, hi = 1000
    for (let i = 0; i < 100; i++) {
        const mid = (lo + hi) / 2
        if (tCDF(mid, df) < targetP) lo = mid
        else hi = mid
    }
    return (lo + hi) / 2
}

// Regularized lower incomplete gamma function P(s,x) - series expansion for
// x < s+1, continued fraction otherwise - used to get the chi-square CDF
function gammaincSeries(s: number, x: number): number {
    let sum = 1 / s
    let term = sum
    let n = 1
    while (Math.abs(term) > Math.abs(sum) * 1e-16 && n < 500) {
        term *= x / (s + n)
        sum += term
        n++
    }
    return sum * Math.exp(-x + s * Math.log(x) - logGamma(s))
}
function gammaincCF(s: number, x: number): number {
    const FPMIN = 1e-300
    let b = x + 1 - s
    let c = 1 / FPMIN
    let d = 1 / b
    let h = d
    for (let i = 1; i <= 500; i++) {
        const an = -i * (i - s)
        b += 2
        d = an * d + b
        if (Math.abs(d) < FPMIN) d = FPMIN
        c = b + an / c
        if (Math.abs(c) < FPMIN) c = FPMIN
        d = 1 / d
        const del = d * c
        h *= del
        if (Math.abs(del - 1) < 1e-16) break
    }
    return Math.exp(-x + s * Math.log(x) - logGamma(s)) * h
}
function gammaincP(s: number, x: number): number {
    if (x < 0 || s <= 0) return 0
    if (x === 0) return 0
    if (x < s + 1) return gammaincSeries(s, x)
    return 1 - gammaincCF(s, x)
}
function chiSquareCDF(x: number, df: number): number {
    return gammaincP(df / 2, x / 2)
}

// z critical values for the confidence levels offered in the select inputs
// below (used by Sample Size and Margin of Error, which - unlike Confidence
// Interval - operate on proportions rather than a small sample's mean, so a
// normal approximation is the standard, widely-used approach)
const Z_CRITICAL: Record<string, number> = {
    '0.80': 1.2816, '0.85': 1.4395, '0.90': 1.645, '0.95': 1.96, '0.98': 2.326, '0.99': 2.576,
}

function parseNumberList(raw: unknown): number[] {
    return String(raw ?? '')
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .map(Number)
        .filter((v) => !isNaN(v))
}

// ============================================================
// 1. Confidence Interval Calculator (for a mean, t-based)
// ============================================================
export function confidenceIntervalCalculator(params: {
    sample_mean: unknown; sample_std_dev: unknown; sample_size: unknown; confidence_level: unknown
}): { lower_bound: number; upper_bound: number; margin_of_error: number } {
    const mean = Number(params.sample_mean) || 0
    const sd = Math.max(Number(params.sample_std_dev) || 0, 0)
    const n = Math.max(Math.round(Number(params.sample_size)) || 2, 2)
    const confidence = Number(params.confidence_level) || 0.95
    const df = n - 1
    const tCrit = tCritical(confidence, df)
    const margin = tCrit * (sd / Math.sqrt(n))
    return { lower_bound: mean - margin, upper_bound: mean + margin, margin_of_error: margin }
}

// ============================================================
// 2. Sample Size Calculator (for estimating a population proportion)
// ============================================================
export function sampleSizeCalculator(params: {
    confidence_level: unknown; margin_of_error: unknown; population_proportion: unknown; population_size: unknown
}): { required_sample_size: number } {
    const confidence = Number(params.confidence_level) || 0.95
    const z = Z_CRITICAL[String(confidence)] ?? 1.96
    const e = Math.max(Number(params.margin_of_error) || 0.05, 0.0001)
    const p = Math.min(Math.max(Number(params.population_proportion) || 0.5, 0), 1)
    let n0 = (z * z * p * (1 - p)) / (e * e)
    const popSize = Number(params.population_size)
    if (popSize && popSize > 0) {
        n0 = n0 / (1 + (n0 - 1) / popSize)
    }
    return { required_sample_size: Math.ceil(n0) }
}

// ============================================================
// 3. Margin of Error Calculator (for a survey/proportion, given n)
// ============================================================
export function marginOfErrorCalculator(params: {
    sample_size: unknown; sample_proportion: unknown; confidence_level: unknown
}): { margin_of_error_percent: number } {
    const n = Math.max(Math.round(Number(params.sample_size)) || 1, 1)
    const p = Math.min(Math.max(Number(params.sample_proportion) || 0.5, 0), 1)
    const confidence = Number(params.confidence_level) || 0.95
    const z = Z_CRITICAL[String(confidence)] ?? 1.96
    const moe = z * Math.sqrt((p * (1 - p)) / n)
    return { margin_of_error_percent: moe * 100 }
}

// ============================================================
// 4. One-Sample T-Test Calculator
// ============================================================
function tTestPValue(tStat: number, df: number, tail: string): number {
    const cdf = tCDF(Math.abs(tStat), df)
    if (tail === 'left') return tStat <= 0 ? 1 - cdf : cdf
    if (tail === 'right') return tStat >= 0 ? 1 - cdf : cdf
    return 2 * (1 - cdf)
}
export function oneSampleTTestCalculator(params: {
    sample_mean: unknown; hypothesized_mean: unknown; sample_std_dev: unknown; sample_size: unknown; tail: unknown
}): { t_statistic: number; degrees_of_freedom: number; p_value: number } {
    const mean = Number(params.sample_mean) || 0
    const hyp = Number(params.hypothesized_mean) || 0
    const sd = Math.max(Number(params.sample_std_dev) || 0.0001, 0.0001)
    const n = Math.max(Math.round(Number(params.sample_size)) || 2, 2)
    const df = n - 1
    const tStat = (mean - hyp) / (sd / Math.sqrt(n))
    const tail = String(params.tail || 'two')
    return { t_statistic: tStat, degrees_of_freedom: df, p_value: tTestPValue(tStat, df, tail) }
}

// ============================================================
// 5. Two-Sample T-Test Calculator (independent samples, Welch's t-test -
// does not assume equal population variances, the more general/robust
// default most real-world comparisons need)
// ============================================================
export function twoSampleTTestCalculator(params: {
    mean1: unknown; std_dev1: unknown; sample_size1: unknown
    mean2: unknown; std_dev2: unknown; sample_size2: unknown
    tail: unknown
}): { t_statistic: number; degrees_of_freedom: number; p_value: number } {
    const m1 = Number(params.mean1) || 0
    const s1 = Math.max(Number(params.std_dev1) || 0.0001, 0.0001)
    const n1 = Math.max(Math.round(Number(params.sample_size1)) || 2, 2)
    const m2 = Number(params.mean2) || 0
    const s2 = Math.max(Number(params.std_dev2) || 0.0001, 0.0001)
    const n2 = Math.max(Math.round(Number(params.sample_size2)) || 2, 2)

    const v1 = (s1 * s1) / n1
    const v2 = (s2 * s2) / n2
    const se = Math.sqrt(v1 + v2)
    const tStat = (m1 - m2) / se
    const df = Math.pow(v1 + v2, 2) / (Math.pow(v1, 2) / (n1 - 1) + Math.pow(v2, 2) / (n2 - 1))
    const tail = String(params.tail || 'two')
    return { t_statistic: tStat, degrees_of_freedom: df, p_value: tTestPValue(tStat, df, tail) }
}

// ============================================================
// 6. Chi-Square Goodness-of-Fit Test Calculator
// ============================================================
export function chiSquareGoodnessOfFitCalculator(params: {
    observed_values: unknown; expected_values: unknown
}): { chi_square_statistic: number; degrees_of_freedom: number; p_value: number } {
    const observed = parseNumberList(params.observed_values)
    const expected = parseNumberList(params.expected_values)
    const count = Math.min(observed.length, expected.length)
    let stat = 0
    for (let i = 0; i < count; i++) {
        if (expected[i] > 0) stat += Math.pow(observed[i] - expected[i], 2) / expected[i]
    }
    const df = Math.max(count - 1, 1)
    const p = 1 - chiSquareCDF(stat, df)
    return { chi_square_statistic: stat, degrees_of_freedom: df, p_value: Math.max(p, 0) }
}

// ============================================================
// 7. P-Value Calculator (from a Z-score)
// ============================================================
export function pValueFromZCalculator(params: { z_score: unknown; tail: unknown }): { p_value: number } {
    const z = Number(params.z_score) || 0
    const tail = String(params.tail || 'two')
    const cdf = normalCDF(z)
    let p: number
    if (tail === 'left') p = cdf
    else if (tail === 'right') p = 1 - cdf
    else p = 2 * (1 - normalCDF(Math.abs(z)))
    return { p_value: Math.min(Math.max(p, 0), 1) }
}

// ============================================================
// 8. Correlation Coefficient Calculator (Pearson r)
// ============================================================
const CORR_STRONG_POS: Record<string, string> = {
    en: 'Strong Positive', ru: 'Сильная положительная', de: 'Stark Positiv', es: 'Fuerte Positiva', fr: 'Forte Positive', it: 'Forte Positiva', pl: 'Silna Dodatnia', lv: 'Spēcīga Pozitīva',
}
const CORR_MODERATE_POS: Record<string, string> = {
    en: 'Moderate Positive', ru: 'Умеренная положительная', de: 'Mäßig Positiv', es: 'Moderada Positiva', fr: 'Modérée Positive', it: 'Moderata Positiva', pl: 'Umiarkowana Dodatnia', lv: 'Mērena Pozitīva',
}
const CORR_WEAK: Record<string, string> = {
    en: 'Weak / Little to No Relationship', ru: 'Слабая / почти нет связи', de: 'Schwach / kaum ein Zusammenhang', es: 'Débil / Poca o Ninguna Relación', fr: 'Faible / Peu ou Pas de Relation', it: 'Debole / Nessuna Relazione', pl: 'Słaba / Brak Zależności', lv: 'Vāja / Gandrīz Nav Sakarības',
}
const CORR_MODERATE_NEG: Record<string, string> = {
    en: 'Moderate Negative', ru: 'Умеренная отрицательная', de: 'Mäßig Negativ', es: 'Moderada Negativa', fr: 'Modérée Négative', it: 'Moderata Negativa', pl: 'Umiarkowana Ujemna', lv: 'Mērena Negatīva',
}
const CORR_STRONG_NEG: Record<string, string> = {
    en: 'Strong Negative', ru: 'Сильная отрицательная', de: 'Stark Negativ', es: 'Fuerte Negativa', fr: 'Forte Négative', it: 'Forte Negativa', pl: 'Silna Ujemna', lv: 'Spēcīga Negatīva',
}
function pearsonR(xs: number[], ys: number[]): number {
    const n = xs.length
    const xbar = xs.reduce((a, b) => a + b, 0) / n
    const ybar = ys.reduce((a, b) => a + b, 0) / n
    let num = 0, dx = 0, dy = 0
    for (let i = 0; i < n; i++) {
        num += (xs[i] - xbar) * (ys[i] - ybar)
        dx += (xs[i] - xbar) ** 2
        dy += (ys[i] - ybar) ** 2
    }
    if (dx === 0 || dy === 0) return 0
    return num / Math.sqrt(dx * dy)
}
export function correlationCoefficientCalculator(params: {
    x_values: unknown; y_values: unknown; language: unknown
}): { correlation_coefficient: number; r_squared: number; interpretation: string } {
    const xs = parseNumberList(params.x_values)
    const ys = parseNumberList(params.y_values)
    const n = Math.min(xs.length, ys.length)
    const r = n >= 2 ? pearsonR(xs.slice(0, n), ys.slice(0, n)) : 0
    let bucket: Record<string, string>
    if (r >= 0.7) bucket = CORR_STRONG_POS
    else if (r >= 0.3) bucket = CORR_MODERATE_POS
    else if (r > -0.3) bucket = CORR_WEAK
    else if (r > -0.7) bucket = CORR_MODERATE_NEG
    else bucket = CORR_STRONG_NEG
    return { correlation_coefficient: r, r_squared: r * r, interpretation: t(bucket, params.language) }
}

// ============================================================
// 9. Linear Regression Calculator (simple linear regression y = a + bx)
// ============================================================
export function linearRegressionCalculator(params: {
    x_values: unknown; y_values: unknown
}): { slope: number; intercept: number; r_squared: number; equation: string } {
    const xs = parseNumberList(params.x_values)
    const ys = parseNumberList(params.y_values)
    const n = Math.min(xs.length, ys.length)
    if (n < 2) return { slope: 0, intercept: 0, r_squared: 0, equation: 'y = 0' }
    const xsN = xs.slice(0, n)
    const ysN = ys.slice(0, n)
    const xbar = xsN.reduce((a, b) => a + b, 0) / n
    const ybar = ysN.reduce((a, b) => a + b, 0) / n
    let num = 0, den = 0
    for (let i = 0; i < n; i++) {
        num += (xsN[i] - xbar) * (ysN[i] - ybar)
        den += (xsN[i] - xbar) ** 2
    }
    const slope = den === 0 ? 0 : num / den
    const intercept = ybar - slope * xbar
    const r = pearsonR(xsN, ysN)
    const equation = `y = ${slope.toFixed(4)}x ${intercept >= 0 ? '+' : '-'} ${Math.abs(intercept).toFixed(4)}`
    return { slope, intercept, r_squared: r * r, equation }
}

// ============================================================
// 10. Standard Error Calculator (standard error of the mean)
// ============================================================
export function standardErrorCalculator(params: { sample_std_dev: unknown; sample_size: unknown }): { standard_error: number } {
    const sd = Math.max(Number(params.sample_std_dev) || 0, 0)
    const n = Math.max(Math.round(Number(params.sample_size)) || 1, 1)
    return { standard_error: sd / Math.sqrt(n) }
}

// ============================================================
// 11. Effect Size (Cohen's d) Calculator
// ============================================================
const EFFECT_NEGLIGIBLE: Record<string, string> = {
    en: 'Negligible', ru: 'Незначительный', de: 'Vernachlässigbar', es: 'Insignificante', fr: 'Négligeable', it: 'Trascurabile', pl: 'Znikomy', lv: 'Nenozīmīgs',
}
const EFFECT_SMALL: Record<string, string> = {
    en: 'Small Effect', ru: 'Малый эффект', de: 'Kleiner Effekt', es: 'Efecto Pequeño', fr: 'Effet Faible', it: 'Effetto Piccolo', pl: 'Mały Efekt', lv: 'Mazs Efekts',
}
const EFFECT_MEDIUM: Record<string, string> = {
    en: 'Medium Effect', ru: 'Средний эффект', de: 'Mittlerer Effekt', es: 'Efecto Medio', fr: 'Effet Moyen', it: 'Effetto Medio', pl: 'Średni Efekt', lv: 'Vidējs Efekts',
}
const EFFECT_LARGE: Record<string, string> = {
    en: 'Large Effect', ru: 'Большой эффект', de: 'Großer Effekt', es: 'Efecto Grande', fr: 'Effet Important', it: 'Effetto Grande', pl: 'Duży Efekt', lv: 'Liels Efekts',
}
export function effectSizeCohensDCalculator(params: {
    mean1: unknown; std_dev1: unknown; sample_size1: unknown
    mean2: unknown; std_dev2: unknown; sample_size2: unknown
    language: unknown
}): { cohens_d: number; interpretation: string } {
    const m1 = Number(params.mean1) || 0
    const s1 = Math.max(Number(params.std_dev1) || 0, 0)
    const n1 = Math.max(Math.round(Number(params.sample_size1)) || 2, 2)
    const m2 = Number(params.mean2) || 0
    const s2 = Math.max(Number(params.std_dev2) || 0, 0)
    const n2 = Math.max(Math.round(Number(params.sample_size2)) || 2, 2)
    const pooledSD = Math.sqrt(((n1 - 1) * s1 * s1 + (n2 - 1) * s2 * s2) / (n1 + n2 - 2)) || 0.0001
    const d = (m1 - m2) / pooledSD
    const abs = Math.abs(d)
    let bucket: Record<string, string>
    if (abs < 0.2) bucket = EFFECT_NEGLIGIBLE
    else if (abs < 0.5) bucket = EFFECT_SMALL
    else if (abs < 0.8) bucket = EFFECT_MEDIUM
    else bucket = EFFECT_LARGE
    return { cohens_d: d, interpretation: t(bucket, params.language) }
}

// ============================================================
// 12. Relative Risk & Odds Ratio Calculator (2x2 contingency table)
// ============================================================
export function relativeRiskOddsRatioCalculator(params: {
    exposed_with_event: unknown; exposed_without_event: unknown
    unexposed_with_event: unknown; unexposed_without_event: unknown
}): { relative_risk: number; odds_ratio: number } {
    const a = Math.max(Number(params.exposed_with_event) || 0, 0)
    const b = Math.max(Number(params.exposed_without_event) || 0, 0)
    const c = Math.max(Number(params.unexposed_with_event) || 0, 0)
    const d = Math.max(Number(params.unexposed_without_event) || 0, 0)
    const exposedRisk = a + b > 0 ? a / (a + b) : 0
    const unexposedRisk = c + d > 0 ? c / (c + d) : 0
    const rr = unexposedRisk > 0 ? exposedRisk / unexposedRisk : 0
    const or = b * c > 0 ? (a * d) / (b * c) : 0
    return { relative_risk: rr, odds_ratio: or }
}
